import {ElementCursor, ElementProperties, ElementView} from '@app/modules/svg/element/ElementView';
import {GraphicView} from '@app/modules/svg/element/complex/cartesian/GraphicView';
import {ElementType} from '@app/modules/svg/dataSource/constant/ElementType';
import {Container} from '@app/modules/svg/Container';
import {Point} from '@app/modules/svg/model/Point';
import {NodeEditable} from '@app/modules/svg/element/shape/pointed/NodeEditable';
import {MoveDrawable} from '@app/modules/svg/service/tool/draw/type/MoveDrawable';
import {Rect} from '@app/modules/svg/model/Rect';

export class TanCursor extends ElementCursor {}

export interface TanProperties extends ElementProperties {
  periods: number;
}

export class TanView extends GraphicView implements NodeEditable, MoveDrawable {
  protected static readonly BASE_POINT_RADIUS: number = 7;

  protected override svgElement: SVGGElement = document.createElementNS(ElementView.svgURI, 'g');
  private svgTanElement: SVGPathElement = document.createElementNS(ElementView.svgURI, 'path');
  protected svgBasePointElement: SVGCircleElement = document.createElementNS(ElementView.svgURI, 'circle');
  protected svgAsymptotesElement: SVGGElement = document.createElementNS(ElementView.svgURI, 'g');

  protected override _type: ElementType = ElementType.TAN;

  protected override _properties: TanProperties;

  private _lastBasePoint: Point;
  private _lastBranchPoint: Point;

  private basePoint: Point;
  private branchPoint: Point;

  constructor(container: Container,
              properties: TanProperties = { periods: 2 },
              basePoint: Point,
              branchPoint: Point,
              ownerId?: string,
              index?: number
  ) {
    super(
      container,
      properties,
      ElementView.toRect(basePoint, branchPoint),
      {x: 0, y: 0},
      (x) => Math.sin(x),
      undefined,
      ownerId,
      index
    );

    this.basePoint = basePoint;
    this.branchPoint = branchPoint;

    this.svgElement.appendChild(this.svgTanElement);
    this.svgElement.appendChild(this.svgBasePointElement);
    this.svgElement.appendChild(this.svgAsymptotesElement);

    this.svgAsymptotesElement.setAttribute('stroke-dasharray', '2 4');
    this.svgAsymptotesElement.setAttribute('stroke-width', '1');

    this.setAttr({
      'stroke-width': 0,
      fill: '#000',
      r: TanView.BASE_POINT_RADIUS
    }, this.svgBasePointElement);

    this.setProperties(properties);

    this.__updateView__();
  }

  public updatePoints(basePoint: Point, branchPoint: Point): void {
    this.basePoint = basePoint;
    this.branchPoint = branchPoint;

    this._rect = ElementView.toRect(basePoint, branchPoint);
    const period = Math.abs(basePoint.x - branchPoint.x) * this._properties.periods;
    this._rect.width += period;
    if (branchPoint.x < basePoint.x) {
      this._rect.x -= period;
    }

    if (branchPoint.x < basePoint.x) {
      this._rect.width *= 2;
    } else {
      this._rect.x -= this._rect.width;
      this._rect.width *= 2;
    }

    let origin: Point;
    if (branchPoint.y < basePoint.y) {
      origin = {
        x: basePoint.x - this._rect.x,
        y: this._rect.height
      };
    } else {
      origin = {
        x: basePoint.x - this._rect.x,
        y: this._rect.height
      };
    }

    if (branchPoint.y > basePoint.y) {
      this._rect.y -= this._rect.height;
    }
    this._rect.height *= 2;

    this.setOrigin(origin);
    this.f = this.tanFrom2Points(basePoint, branchPoint, this._mainStepPhysicalUnit);
  }

  public override __updateView__(): void {
    if (!this.basePoint) {
      return;
    }

    this.svgAsymptotesElement.innerHTML = '';

    /* create asymptotes */
    const half_periods = Math.floor(this._properties.periods / 2);
    const delta = Math.abs(this.branchPoint.x - this.basePoint.x);

    for (let i = -half_periods; i < half_periods * 2 + 1; i++) {
      const x = this.basePoint.x + delta * 2 * i - delta;
      const y1 = this._rect.y;
      const y2 = this._rect.y + this._rect.height;

      const asymptote = document.createElementNS(ElementView.svgURI, 'line');
      asymptote.setAttribute('x1', x + '');
      asymptote.setAttribute('y1', y1 + '');
      asymptote.setAttribute('x2', x + '');
      asymptote.setAttribute('y2', y2 + '');

      this.svgAsymptotesElement.appendChild(asymptote);
    }

    this.setAttr({
      d: this._path.toString()
    }, this.svgTanElement);

    this.setAttr({
      cx: this.basePoint.x,
      cy: this.basePoint.y,
    }, this.svgBasePointElement);
  }

  public override __drawSize__(rect: Rect) {
    const basePoint: Point = {
      x: rect.x,
      y: rect.y,
    };

    const branchPoint: Point = {
      x: rect.x + rect.width,
      y: rect.y + rect.height,
    };

    super.__drawSize__(rect);

    this.updatePoints(basePoint, branchPoint);
  }

  public override __setRect__(rect: Rect) {
    let dw: number = 1;
    let dh: number = 1;

    if (this._lastRect.width !== 0) {
      dw = rect.width / (this._lastRect.width);
    }
    if (this._lastRect.height !== 0) {
      dh = rect.height / (this._lastRect.height);
    }

    this.updatePoints({
      x: rect.x + Math.abs(this._lastBasePoint.x - rect.x) * dw,
      y: rect.y + Math.abs(this._lastBasePoint.y - rect.y) * dh
    }, {
      x: rect.x + Math.abs(this._lastBranchPoint.x - rect.x) * dw,
      y: rect.y + Math.abs(this._lastBranchPoint.y - rect.y) * dh
    });
  }

  public override __correct__(refPoint: Point, lastRefPoint: Point): void {
    const delta: Point = this.__getCorrectionDelta__(refPoint, lastRefPoint);
    this.basePoint.x += delta.x;
    this.basePoint.y += delta.y;

    this.branchPoint.x += delta.x;
    this.branchPoint.y += delta.y;

    super.__correct__(refPoint, lastRefPoint);

    this.updatePoints(this.basePoint, this.branchPoint);
  }

  public override __drag__(delta: Point): void {
    super.__drag__(delta);

    this._rect.x += delta.x;
    this._rect.y += delta.y;

    const basePoint = {
      x: this.basePoint.x + delta.x,
      y: this.basePoint.y + delta.y,
    };
    const branchPoint = {
      x: this.branchPoint.x + delta.x,
      y: this.branchPoint.y + delta.y,
    };

    this.updatePoints(basePoint, branchPoint);
  }

  public override __fixRect__(): void {
    super.__fixRect__();
    this._lastBasePoint = Object.assign({}, this.basePoint);
    this._lastBranchPoint = Object.assign({}, this.branchPoint);
  }

  public isComplete(): boolean {
    return TanView.pointsDistance(this.basePoint, this.branchPoint) > 10;
  }

  public get editablePoints(): Point[] {
    return [this.basePoint, this.branchPoint];
  }
  public get editablePointsCount(): number {
    return 2;
  }
  public replacePoint(index: number, point: Point): void {
    if (index === 0) {
      this.basePoint = point;
    } else if (index === 1) {
      this.branchPoint = point;
    }

    this.updatePoints(this.basePoint, this.branchPoint);
  }

  private resetOrigin(): void {
    let origin: Point;
    if (this.branchPoint.y < this.basePoint.y) {
      origin = {
        x: this._rect.width / 2,
        y: this._rect.height
      };
    } else {
      origin = {
        x: this._rect.width / 2,
        y: 0
      };
    }

    const delta: Point = ElementView.delta(origin, this._origin);

    this.basePoint.x = this._lastBasePoint.x + delta.x;
    this.basePoint.y = this._lastBasePoint.y + delta.y;

    this.branchPoint.x = this._lastBranchPoint.x + delta.x;
    this.branchPoint.y = this._lastBranchPoint.y + delta.y;

    this.setOrigin(origin);
  }

  /**
   * y = a * tan(b * x)
   * */
  private tanFrom2Points(basePoint: Point, branchPoint: Point, mainStepPhysicalUnit: number): (x: number) => number {
    const a = (branchPoint.y - basePoint.y) / mainStepPhysicalUnit;
    const b = (branchPoint.x - basePoint.x) / mainStepPhysicalUnit;

    return (inputX: number): number => a / 2 * Math.tan(Math.PI / 2 / b * inputX);
  }

  public override toJSON(): any {
    const json: any = super.toJSON();
    json.basePoint = this.basePoint;
    json.branchPoint = this.branchPoint;
    return json;
  }

  public override fromJSON(json: any): void {
    this.basePoint = json.basePoint;
    this.branchPoint = json.branchPoint;
    this.f = this.tanFrom2Points(json.basePoint, json.branchPoint, this._mainStepPhysicalUnit);
    super.fromJSON(json);
  };
}
